<template>
  <div class="sidebar">
    <!-- Hamburger menu start-->
    <input ref="menuToggleCheck" type="checkbox" />
    <span></span>
    <span></span>
    <span></span>

    <!-- Hamburger menu end-->

    <div class="nav-container animate bg-primary-lightest" :class="{ expanded: !isMenuForceCondensed && !isCondensed }">
      <MenuList
        v-if="isLeftMenuVisible"
        class="mx-3 menu-list w-12 overflow-hidden animate"
        :class="{ 'lg:w-full': !isMenuForceCondensed && !isCondensed }"
        :items="leftMenuItemsManipulated"
        @itemClick="removeLeftMenuOpenCheck"
      />

      <DocumentSearch
        v-if="isLeftMenuVisible && !isCondensed && !isMenuForceCondensed"
        class="-ml-8 hidden lg:block mt-4"
      />

      <div class="hidden sm:block mt-auto">
        <img
          v-if="settingsStore.customerDesign.logo"
          :src="settingsStore.customerDesign.logo"
          :alt="settingsStore.customerDesign.name"
          class="m-auto opacity-50 mb-10"
          style="max-width: 80%; max-height: 150px"
        />

        <div class="flex flex-col-reverse" :class="{ 'lg:flex-row': !isCondensed && !isMenuForceCondensed }">
          <div
            class="opacity-60 overflow-hidden mt-4 px-2 -mb-6 animate"
            :class="{ 'lg:mt-0 lg:px-0 lg:mb-0': !isCondensed && !isMenuForceCondensed }"
            style="font-size: 8px; line-height: 12px"
          >
            <span class="my-1 hidden" :class="{ 'lg:block': !isCondensed && !isMenuForceCondensed }">Powered by</span>
            <a
              v-if="settingsStore.resellerDesign.logo"
              :href="settingsStore.resellerDesign.url"
              target="_blank"
              tabindex="-1"
              aria-hidden="true"
            >
              <img
                v-if="settingsStore.resellerDesign.logo"
                :src="settingsStore.resellerDesign.logo"
                :alt="settingsStore.resellerDesign.name"
                style="max-height: 22px"
              />
            </a>
            <a v-else href="https://nordiccloud.dk" target="_blank" tabindex="-1" aria-hidden="true">
              <img
                src="https://assets.nordiccloud.dk/logos/nct/nct_logo_2025.png"
                style="max-height: 22px"
                alt="Nordic Cloud Technologies"
              />
            </a>
          </div>
          <div v-if="!isMenuForceCondensed" class="toggler-container">
            <button class="toggler ml-auto mr-0" :class="{ 'mr-auto': isCondensed }" @click="toogleLeftMenu">
              <svg viewBox="0 0 8 12" class="w-2 fill-current mx-auto opacity-60" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 6l6-6 1 1-4 5 4 5-1 1-6-6z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MenuList from "@/components/Navigation/MenuList";
import { CookieHelper } from "@/helpers/utilities/CookieHelper";
import { useSettingsStore } from "@/stores/settings";
import { useNavigationStore } from "@/stores/navigation";
import { useFeaturesStore } from "@/stores/features";

export default {
  name: "LeftPanel",

  data: function () {
    return {
      isCondensed: false
    };
  },

  props: {
    isLeftMenuVisible: {
      type: Boolean,
      default: true
    },
    isDocumentSearchVisible: {
      type: Boolean,
      default: true
    }
  },

  setup() {
    return {
      settingsStore: useSettingsStore(),
      navigationStore: useNavigationStore(),
      featuresStore: useFeaturesStore()
    };
  },

  async mounted() {
    if (this.isLeftMenuVisible) {
      await this.navigationStore.loadLeftMenu();
    }

    if (!this.isMenuForceCondensed) {
      const cookieValue = CookieHelper.getCookieForIsLeftMenuMinimized();
      this.isCondensed = cookieValue && cookieValue !== "" ? cookieValue : this.isCondensed;
    }
  },

  computed: {
    leftMenuItemsManipulated() {
      if (this.isCondensed && this.featuresStore.isFeatureEnabled("DocumentsSearch")) {
        const manipulatedItems = this.navigationStore.leftMenu;
        return manipulatedItems.concat([
          {
            id: "171717",
            icon: "https://assets.cdn.nordiccloud.dk/icons/fontawesome/search.svg",
            sort: 17171717,
            title: this.$t("Search.SearchDocuments"),
            uri: {
              action: "app",
              uri: "/documentssearch"
            }
          }
        ]);
      }
      return this.navigationStore.leftMenu;
    },

    isMenuForceCondensed() {
      return this.settingsStore.navigationConfiguration?.forceLeftMenuCondensed || false;
    },

    appVersion() {
      return import.meta.env.VITE_APP_VERSION;
    }
  },

  methods: {
    toogleLeftMenu() {
      this.isCondensed = !this.isCondensed;
      CookieHelper.setCookieForIsLeftMenuMinimized(this.isCondensed);
    },

    removeLeftMenuOpenCheck() {
      this.$refs.menuToggleCheck.checked = false;
    }
  },

  components: {
    MenuList,
    DocumentSearch: () => import("@/components/Documents/Functions/DocumentSearch")
  }
};
</script>

<style lang="postcss" scoped>
/* https://codepen.io/erikterwan/pen/EVzeRP */

.sidebar {
  @apply relative w-0;
  top: -40px;

  @screen md {
    @apply w-auto top-0;
  }
}

.animate {
  transition: 0.3s cubic-bezier(0.77, 0.2, 0.05, 1);
}

.sidebar > .nav-container {
  @apply overflow-auto border-r border-black-10% flex-shrink-0 absolute z-50 w-18 h-full;

  transform-origin: 0% 0%;
  transform: translate(-100%, 0);
  @apply mt-4 pt-4 py-8;

  @screen md {
    @apply flex flex-col static w-18 pt-7 mt-0 overflow-hidden;
    transform: none;
  }

  &.expanded {
    @screen lg {
      @apply w-64 px-4;
    }
  }
}

.sidebar > input {
  display: block;
  width: 40px;
  height: 32px;
  position: absolute;
  top: -5px;
  left: 11px;
  cursor: pointer;
  opacity: 0;
  z-index: 12;
  touch-callout: none;

  @screen md {
    @apply hidden;
  }
}

.sidebar input:checked ~ .nav-container {
  transform: none;
}

.logo {
  @apply h-18 object-contain object-bottom opacity-25;

  @screen lg {
    .sidebar > .nav-container.expanded & {
      @apply object-left-bottom;
    }
  }
}

.toggler {
  @apply w-12 h-12 hidden rounded-full bg-white shadow;

  @screen lg {
    @apply block;

    .sidebar > .nav-container:not(.expanded) & {
      svg {
        transform: rotate(180deg);
      }
    }

    .sidebar > .nav-container.expanded & {
      @apply w-12 h-12;
    }
  }

  &:focus {
    @apply outline-none shadow-lg;
  }
}

.navheader {
  @screen md {
    @apply hidden;
  }

  @screen lg {
    .sidebar > .nav-container.expanded & {
      @apply block;
    }
  }
}

.menu-list {
  @screen md {
    @apply px-1;
  }

  @screen lg {
    .sidebar > .nav-container.expanded & {
      @apply px-0;
    }
  }
}

.toggler-container {
  @screen md {
    @apply mx-auto;
  }
  @screen lg {
    .sidebar > .nav-container.expanded & {
      @apply ml-auto mr-0;
    }
  }
}

/* Hamburger menu */
.sidebar > span {
  display: block;
  width: 23px;
  height: 3px;
  margin-bottom: 5px;
  margin-left: 15px;
  position: relative;

  background: white;
  border-radius: 3px;

  z-index: 1;
  transform-origin: 0px 4px;

  transition:
    transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1),
    opacity 0.55s ease;

  @screen md {
    @apply hidden;
  }
}

.sidebar > span:first-child {
  transform-origin: 0% 0%;
}

.sidebar > span:nth-last-child(2) {
  transform-origin: 0% -2px;
}

.sidebar > input:checked ~ span {
  opacity: 1;
  transform: rotate(45deg) translate(-2px, -2px);
}

.sidebar > input:checked ~ span:nth-last-child(3) {
  opacity: 0;
  transform: rotate(0deg) scale(0.2, 0.2);
}

.sidebar > input:checked ~ span:nth-last-child(2) {
  transform: rotate(-45deg) translate(0, 0);
}
</style>
